/**
 * O LUCRO POR LEAD — o painel que o dono pediu no dashboard.
 *
 * ### O número herói é o da operação INTEIRA
 *
 * Comissão total do dia ÷ pessoas em todos os grupos monitorados. A base é sempre o total:
 * é assim que este mercado lê a qualidade do lead, e é a frase do dono ("base total
 * sempre!!"). O detalhamento por grupo existe, mas vem DEPOIS e menor — ele responde "qual
 * grupo traz lead melhor" para quem já separou o número na mão, o que hoje é a exceção. Por
 * isso o painel tem que ficar completo e bonito com `groups` vazio: esse é o estado normal,
 * não um estado incompleto esperando ser preenchido.
 *
 * ### O que este arquivo NÃO faz
 *
 * Não calcula lucro por lead e não decide faixa. Os dois chegam prontos (`profitPerLeadMillicents`
 * e `band`), e os LIMITES das faixas não estão escritos aqui — se estivessem, o dia em que o
 * servidor mudasse a régua a tela continuaria desenhando, agora mentindo. A única aritmética
 * do fluxo é o eco do valor que está sendo digitado, e ela mora na gaveta de lançamento.
 *
 * ### "Não lançado" não é "zero"
 *
 * `totalCents === null` quer dizer que ninguém informou a comissão do dia; `0` quer dizer que
 * a comissão foi zero. Um painel que mostrasse R$ 0,00 nos dois casos transformaria
 * esquecimento em fato consumado — e ninguém voltaria para consertar.
 *
 * ### Marketplaces (18/09): o geral é a SOMA, nunca a média
 *
 * A comissão passou a ter dono — Shopee, Amazon e Mercado Livre —, e os três aparecem
 * sempre, inclusive o que não foi lançado. O número herói continua sendo **o geral**: a soma
 * das comissões sobre a mesma base de pessoas. Ele é exatamente a soma dos parciais porque o
 * denominador **não se divide** entre marketplaces (a mesma pessoa no grupo pode comprar em
 * dois no mesmo dia), e a tela diz isso em voz alta — a leitura errada mais provável aqui é
 * achar que o geral é a média dos três.
 *
 * A **faixa** (o semáforo) fica só no geral. A régua é do mercado de afiliado da Shopee; os
 * outros dois têm comissão e ticket diferentes, e um semáforo que erra ensina a ignorar
 * semáforo.
 */
import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardDescription,
  CardTitle,
  ErrorState,
  MetricValue,
  Skeleton,
  StatTile,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableRowHeader,
  formatNumber,
} from '@mgdlead/ui';
import { can } from '@mgdlead/shared';

import { useWorkspace } from '../../../features/workspace/WorkspaceContext';
import {
  EARNINGS_WRITE_PERMISSION,
  useEarningsDay,
  useEarningsRange,
  type DayEarnings,
  type EarningsBand,
} from '../../../lib/api';
import {
  BANDA,
  FONTE_DA_FAIXA,
  MARKETPLACE,
  POR_QUE_SEM_FAIXA_POR_MARKETPLACE,
  diasAntes,
  formatarLucroPorLead,
  lancados,
  lucroPorLeadEmReais,
  motivoDoDenominador,
  naoLancadoEm,
  participacaoEmTexto,
  reaisDeCentavos,
  rotuloDoDia,
} from '../../../features/earnings/earnings-ui';
import { FormAlert, MissingDataHint } from '@mgdlead/ui';
import { humanizeError } from '../../../lib/errors';
import { LancamentoDrawer } from './LancamentoDrawer';

export interface LucroPorLeadPanelProps {
  /**
   * O dia do negócio de hoje, **resolvido pelo servidor** no fuso do workspace (ele chega em
   * `analytics.overview.period.to`). `null` enquanto a primeira resposta não voltou: o painel
   * não inventa "hoje" com o relógio do navegador, senão às 22h em São Paulo ele pediria o
   * dia de amanhã e mostraria um dia vazio que na verdade nem começou.
   */
  hoje: string | null;
}

export function LucroPorLeadPanel({ hoje }: LucroPorLeadPanelProps) {
  const { workspace } = useWorkspace();
  const [lancando, setLancando] = useState(false);

  const podeLancar = can(workspace.role, EARNINGS_WRITE_PERMISSION);
  const query = useEarningsDay(workspace.id, hoje);
  const dia = query.data;

  if (query.isError) {
    return (
      <Card>
        <CardTitle>Lucro por lead</CardTitle>
        <ErrorState
          className="mt-3"
          title="Não foi possível carregar o lucro por lead"
          description={humanizeError(query.error)}
          onRetry={() => void query.refetch()}
        />
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <CardTitle>Lucro por lead</CardTitle>
          <CardDescription>
            A comissão do dia dividida por todas as pessoas nos grupos monitorados. A base é sempre
            a operação inteira.
          </CardDescription>
        </div>
        {hoje ? (
          <p className="nums shrink-0 text-xs text-muted-foreground">{rotuloDoDia(hoje, hoje)}</p>
        ) : null}
      </div>

      {hoje === null || query.isPending || dia === undefined ? (
        <PainelCarregando />
      ) : (
        <>
          <Heroi dia={dia} onLancar={podeLancar ? () => setLancando(true) : undefined} />
          <PorMarketplace dia={dia} />
          <JanelaDeSeteDias hoje={hoje} />
          <Avisos dia={dia} />

          <div className="mt-3 grid grid-cols-2 gap-3">
            <StatTile
              size="compact"
              /* A SOMA dos marketplaces lançados — é o "dado geral" que o dono pediu. */
              label="Comissão do dia (todos)"
              format="currency"
              abbreviate="never"
              /*
                Centavos viram reais para exibir — troca de unidade, não conta de métrica.
                `null` continua `null`: dia não lançado mostra travessão, nunca R$ 0,00.
              */
              value={dia.totalCents === null ? null : dia.totalCents / 100}
              noDataReason="A comissão deste dia ainda não foi lançada. É diferente de ter sido lançada como R$ 0,00."
            />
            <StatTile
              size="compact"
              label="Pessoas nos grupos"
              abbreviate="never"
              value={dia.membersEod}
              /* O servidor diz POR QUE não há denominador; a tela não inventa o motivo. */
              noDataReason={
                motivoDoDenominador(dia.membersEodStatus) ??
                'Não há contagem de participantes deste dia. Sem esse denominador não existe lucro por lead — e zero seria uma medição inventada.'
              }
            />
          </div>

          {podeLancar && dia.totalCents === null ? null : podeLancar ? (
            <div className="mt-3 [&>*]:max-md:w-full">
              {/*
                A ÚNICA ação colorida do dashboard (seção 1.5), e a única do painel — nada de
                cinco botões `ghost` iguais em que o certo se perde. No celular ela ocupa a
                largura toda, que é a regra de ação importante (seção 11).
              */}
              <Button variant="primary" onClick={() => setLancando(true)}>
                {dia.totalCents === null ? 'Lançar comissão do dia' : 'Editar a comissão deste dia'}
              </Button>
            </div>
          ) : (
            <p className="mt-3 text-xs text-muted-foreground">
              Seu papel vê o painel, mas não informa a comissão do dia. Peça a quem cuida da
              operação.
            </p>
          )}

          <NaoAtribuida dia={dia} />
          <ComparativoPorGrupo dia={dia} />

          <p className="mt-4 border-t border-border pt-3 text-xs text-subtle-foreground">
            {FONTE_DA_FAIXA}
          </p>
        </>
      )}

      {podeLancar && hoje !== null ? (
        <LancamentoDrawer open={lancando} onOpenChange={setLancando} hoje={hoje} />
      ) : null}
    </Card>
  );
}

function PainelCarregando() {
  return (
    <div role="status" aria-busy="true" className="mt-3 flex flex-col gap-3">
      <span className="sr-only">Carregando o lucro por lead</span>
      <Skeleton className="h-10 w-48" />
      <div className="grid grid-cols-2 gap-3">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    </div>
  );
}

/* ========================================================================== */
/*  O herói                                                                    */
/* ========================================================================== */

function Heroi({
  dia,
  onLancar,
}: {
  dia: DayEarnings;
  /** G6: o botão do "—" sem comissão. Ausente para quem não lança (o texto continua). */
  onLancar?: (() => void) | undefined;
}) {
  return (
    <div className="mt-3">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <MetricValue
          /* `null` continua `null` até a tela: quem não tem denominador não tem número. */
          value={
            dia.profitPerLeadMillicents === null
              ? null
              : lucroPorLeadEmReais(dia.profitPerLeadMillicents)
          }
          format="currency"
          /*
            Quatro casas porque a decisão mora na segunda casa do centavo: com duas, as
            quatro faixas do mercado virariam todas "R$ 0,03" ou "R$ 0,05".
          */
          decimals={4}
          noDataLabel="ainda não há lucro por lead neste dia"
          className="text-3xl font-bold"
        />
        <span className="text-sm text-muted-foreground">por lead</span>
        {dia.band !== null ? <FaixaBadge band={dia.band} /> : null}
      </div>

      {/*
        Cada vazio tem a SUA frase, porque eles mandam fazer coisas diferentes: um pede que
        alguém digite o valor, o outro avisa que não há denominador e não há nada a fazer
        hoje. Uma frase genérica mandaria fazer nada.
      */}
      {dia.totalCents === null ? (
        /*
          G6 (07/10): o vazio com causa conhecida vira frase + botão. "Ainda não lançada" é
          diferente de "lançada como R$ 0,00" — e o conserto está a um toque.
        */
        <MissingDataHint
          className="mt-2"
          action={
            onLancar ? (
              /*
                É a ÚNICA ação colorida do painel (seção 1.5): o botão de baixo some enquanto o
                dia não foi lançado, para não haver dois "Lançar" iguais.
              */
              <Button variant="primary" size="sm" onClick={onLancar}>
                Lançar comissão do dia
              </Button>
            ) : undefined
          }
        >
          A comissão deste dia <strong>ainda não foi lançada</strong> (é diferente de R$ 0,00).
          Lance a comissão do dia para ver o lucro por lead.
        </MissingDataHint>
      ) : dia.membersEod === null ? (
        <p className="mt-2 text-sm text-muted-foreground">
          {reaisDeCentavos(dia.totalCents)} lançados, mas{' '}
          <strong>sem lucro por lead neste dia</strong>:{' '}
          {motivoDoDenominador(dia.membersEodStatus) ??
            'não há contagem de participantes, e mostrar zero seria inventar uma medição.'}
        </p>
      ) : (
        <p className="nums mt-2 text-sm text-muted-foreground">
          {reaisDeCentavos(dia.totalCents)} ÷{' '}
          {formatNumber(dia.membersEod, { abbreviate: 'never' })} pessoas nos grupos monitorados.
        </p>
      )}

      {dia.band !== null ? (
        <p className="mt-1 text-xs text-muted-foreground">{BANDA[dia.band].significado}</p>
      ) : null}
    </div>
  );
}

/* ========================================================================== */
/*  Por marketplace — Shopee, Amazon e Mercado Livre                          */
/* ========================================================================== */

/**
 * O lucro por lead **de cada marketplace**, e o que ele pesa no dia.
 *
 * ### As três decisões desta seção
 *
 * **1. Os três aparecem sempre.** O que não foi lançado aparece dizendo que não foi lançado,
 * e não some — sumir transformaria "ainda não lancei a Amazon" em "a Amazon não existe", e
 * ninguém volta para lançar o que não está pedindo. `null` nunca vira `R$ 0,00`
 * (invariante 8).
 *
 * **2. O número vem pronto.** Nem a divisão, nem a soma dos três, nem a participação são
 * feitas aqui (invariante 3). O geral é a soma dos parciais porque o servidor divide os três
 * pela MESMA base de pessoas — a mesma pessoa no grupo pode comprar na Shopee e na Amazon no
 * mesmo dia, e não é um terço de lead em cada.
 *
 * **3. Nenhuma faixa por marketplace.** A régua é do mercado de afiliado da Shopee; aplicá-la
 * aos três pintaria de vermelho um número que talvez seja ótimo. Aqui há valor e
 * participação, sem julgamento — ver `POR_QUE_SEM_FAIXA_POR_MARKETPLACE`.
 */
function PorMarketplace({ dia }: { dia: DayEarnings }) {
  const comLancamento = lancados(dia.marketplaces);

  return (
    <section className="mt-3 border-t border-border pt-3">
      <h3 className="font-mono text-2xs font-medium uppercase tracking-caps text-subtle-foreground">
        Por marketplace
      </h3>

      <ul className="mt-2 flex flex-col gap-2">
        {dia.marketplaces.map((fatia) => {
          const participacao = participacaoEmTexto(fatia.shareBasisPoints);
          return (
            <li
              key={fatia.marketplace}
              className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 rounded-md bg-surface-subtle px-3.5 py-2.5"
            >
              <span className="text-sm font-medium">{MARKETPLACE[fatia.marketplace]}</span>

              {fatia.amountCents === null ? (
                /* Ausência com a frase do motivo: é uma tarefa, não um resultado. */
                <span className="text-xs text-muted-foreground">
                  {naoLancadoEm(fatia.marketplace)}
                </span>
              ) : (
                <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <MetricValue
                    value={
                      fatia.profitPerLeadMillicents === null
                        ? null
                        : lucroPorLeadEmReais(fatia.profitPerLeadMillicents)
                    }
                    format="currency"
                    decimals={4}
                    noDataLabel="sem contagem de pessoas neste dia"
                    className="text-sm font-semibold"
                  />
                  <span className="text-xs text-muted-foreground">por lead</span>
                  <span className="nums text-xs text-muted-foreground">
                    · {reaisDeCentavos(fatia.amountCents)}
                    {participacao !== null ? ` · ${participacao} do dia` : ''}
                  </span>
                </span>
              )}
            </li>
          );
        })}
      </ul>

      {comLancamento.length > 1 ? (
        <p className="mt-2 text-xs text-muted-foreground">
          {/*
            A frase que impede a leitura errada mais provável desta tela. Os três dividem pela
            MESMA base de pessoas, então o geral é a soma deles — e não uma média.
          */}
          Os {comLancamento.length} dividem pela <strong>mesma</strong> base de pessoas: a mesma
          pessoa no grupo pode comprar em mais de um no mesmo dia. Por isso o lucro por lead geral é
          a soma dos de cima, não a média deles.
        </p>
      ) : null}

      <p className="mt-1 text-xs text-muted-foreground">{POR_QUE_SEM_FAIXA_POR_MARKETPLACE}</p>
    </section>
  );
}

/* ========================================================================== */
/*  A janela de 7 dias — o número que se olha, não o que oscila               */
/* ========================================================================== */

/**
 * O dia de hoje é o herói da tela, mas **sozinho ele engana**: uma promoção boa numa
 * terça e um feriado fraco na quarta mudam o lucro por lead mais do que qualquer mudança
 * na qualidade dos leads. A janela de 7 dias é o número que responde "a operação está
 * melhorando ou piorando".
 *
 * É **razão de somas**, não média de razões — quem soma as razões dá o mesmo peso a um dia
 * de 200 pessoas e a um de 20 mil. Quem faz a conta é o servidor (invariante 3); aqui só se
 * lê `summary`.
 *
 * `daysOutOfAverage` é dito em voz alta: dia sem contagem de participantes fica fora do
 * cálculo, e esconder isso faria a janela parecer mais sólida do que é (invariante 9 — o
 * número carrega o grau de confiança junto).
 */
function JanelaDeSeteDias({ hoje }: { hoje: string }) {
  const { workspace } = useWorkspace();
  const query = useEarningsRange(workspace.id, diasAntes(hoje, 6), hoje);
  const resumo = query.data?.summary;

  /* Sem dia nenhum lançado na janela, a linha não aparece: ela não tem o que dizer. */
  if (resumo === undefined || resumo.daysWithTotal === 0) return null;

  return (
    <div className="mt-3 flex flex-wrap items-baseline gap-x-2 gap-y-1 border-t border-border pt-3">
      <span className="text-xs text-muted-foreground">Últimos 7 dias</span>
      <MetricValue
        value={
          resumo.profitPerLeadMillicents === null
            ? null
            : lucroPorLeadEmReais(resumo.profitPerLeadMillicents)
        }
        format="currency"
        decimals={4}
        noDataLabel="sem base para a média"
        className="text-md font-medium"
      />
      <span className="text-xs text-muted-foreground">por lead</span>
      {resumo.band !== null ? <FaixaBadge band={resumo.band} /> : null}
      <span className="nums text-xs text-muted-foreground">
        · {resumo.daysWithTotal} {resumo.daysWithTotal === 1 ? 'dia lançado' : 'dias lançados'}
        {resumo.daysOutOfAverage > 0
          ? `, ${resumo.daysOutOfAverage} fora da conta por não ter contagem de pessoas`
          : ''}
      </span>

      {/*
        O mesmo recorte da janela, por marketplace — razão de somas sobre o MESMO denominador
        do geral, feita no servidor. `daysWithEntry` vai junto porque um marketplace lançado
        em 1 de 7 dias rende pouco por CONSTRUÇÃO (nos outros seis ele soma zero, e a base
        continua inteira): sem essa contagem ao lado, a diferença seria lida como desempenho.
      */}
      <ul className="nums w-full text-xs text-muted-foreground">
        {resumo.byMarketplace
          .filter((m) => m.totalCents !== null)
          .map((m) => (
            <li key={m.marketplace}>
              {MARKETPLACE[m.marketplace]}:{' '}
              {m.profitPerLeadMillicents === null
                ? '—'
                : `${formatarLucroPorLead(m.profitPerLeadMillicents)}/lead`}{' '}
              · {m.daysWithEntry} {m.daysWithEntry === 1 ? 'dia lançado' : 'dias lançados'}
            </li>
          ))}
      </ul>
    </div>
  );
}

/* ========================================================================== */
/*  Os avisos do servidor — valor implausível se AVISA, não se corrige         */
/* ========================================================================== */

/**
 * Os avisos chegam com a frase pronta e a tela os exibe como vieram.
 *
 * É a metade que o eco do campo não cobre: o eco pega o erro de unidade na hora de digitar,
 * mas quem já gravou R$ 123.456,00 achando que eram R$ 1.234,56 precisa ser avisado no lugar
 * onde ele olha o número. E o aviso é AVISO — a tela não conserta o valor sozinha, senão o
 * painel passa a discordar do que a pessoa digitou sem nunca dizer que discordou.
 *
 * Um aviso por vez, cada um na sua linha: uma lista de três avisos resumida numa frase só é
 * uma frase que ninguém consegue agir em cima.
 */
function Avisos({ dia }: { dia: DayEarnings }) {
  if (dia.warnings.length === 0) return null;

  return (
    <div className="mt-3 flex flex-col gap-2">
      {dia.warnings.map((aviso) => (
        <FormAlert key={`${aviso.code}-${aviso.groupId ?? 'total'}`} tone="warning">
          {aviso.message}
        </FormAlert>
      ))}
    </div>
  );
}

/**
 * A faixa em TRÊS canais: cor, ícone (forma) e a palavra escrita.
 *
 * A palavra não é enfeite: é um painel olhado às 23h, no celular, às vezes por quem não
 * distingue verde de vermelho. Um ponto colorido sozinho custa três segundos de decifração
 * todo dia — e some de vez para uma parte das pessoas.
 */
export function FaixaBadge({ band }: { band: EarningsBand }) {
  const spec = BANDA[band];
  const Icon = spec.icon;
  return (
    <Badge tone={spec.tone} icon={<Icon aria-hidden="true" />}>
      {spec.label}
    </Badge>
  );
}

/* ========================================================================== */
/*  A comissão que não foi atribuída a grupo                                   */
/* ========================================================================== */

/**
 * Um estado NOMEADO — nem erro, nem sumiço. É o mesmo tratamento que a tela de
 * redirecionadores dá ao clique sem origem: o valor tem nome, aparece com o número que o
 * servidor mandou, e quando é zero a tela diz que está tudo distribuído em vez de esconder
 * a linha (esconder ensinaria que o número não existe).
 *
 * Só aparece quando existe ALGUM lançamento por grupo. Sem detalhamento não há lacuna: há o
 * total, e inventar uma "não atribuída" igual ao total transformaria o caminho normal —
 * lançar um valor só — em pendência.
 */
function NaoAtribuida({ dia }: { dia: DayEarnings }) {
  if (dia.coverage.groupsWithEntry === 0) return null;

  return (
    <div className="mt-3 rounded-md bg-surface-subtle px-3.5 py-2.5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="font-mono text-2xs font-medium uppercase tracking-caps text-subtle-foreground">
          Comissão não atribuída a grupo
        </p>
        <MetricValue
          value={dia.unattributedCents === null ? null : dia.unattributedCents / 100}
          format="currency"
          noDataLabel="não dá para dizer: falta o total do dia ou a soma dos grupos"
          className="text-sm font-semibold"
        />
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        {dia.unattributedCents === null
          ? 'Sem o total do dia lançado não dá para dizer quanto sobrou fora dos grupos.'
          : dia.unattributedCents === 0
            ? 'Todo o valor do dia está distribuído entre os grupos informados.'
            : 'É a parte do total do dia que não foi informada em nenhum grupo. Ela continua contando no lucro por lead geral — a base é sempre o total.'}
      </p>
    </div>
  );
}

/* ========================================================================== */
/*  Comparativo por grupo — secundário, e só quando existe                     */
/* ========================================================================== */

/**
 * Qual grupo traz lead melhor.
 *
 * Aparece abaixo do número principal e só quando alguém detalhou por grupo. Duas distinções
 * que a tabela carrega, e que são o motivo de ela existir:
 *
 *  - grupo **sem lançamento** (`amountCents === null`) não é grupo com **zero** lançado;
 *  - grupo **sem contagem de participantes** não tem lucro por lead nenhum — nem zero, nem
 *    o número do dia anterior. Sem denominador, não há medição.
 */
function ComparativoPorGrupo({ dia }: { dia: DayEarnings }) {
  if (dia.coverage.groupsWithEntry === 0) return null;

  const comLancamento = dia.groups.filter((g) => g.amountCents !== null || g.membersEod !== null);
  if (comLancamento.length === 0) return null;

  return (
    <section className="mt-3">
      <h3 className="font-mono text-2xs font-medium uppercase tracking-caps text-subtle-foreground">
        Por grupo
      </h3>
      <p className="mt-1 text-xs text-muted-foreground">
        {dia.coverage.groupsWithEntry} de {dia.coverage.groupsTotal} grupos com lançamento próprio.
        {dia.coverage.groupsWithoutCount > 0
          ? ` ${dia.coverage.groupsWithoutCount} sem contagem de participantes no dia — esses não têm lucro por lead.`
          : ''}
      </p>

      <Table
        caption="Comissão e lucro por lead de cada grupo no dia"
        density="dense"
        className="mt-2"
      >
        <TableHeader>
          <TableRow>
            <TableHead>Grupo</TableHead>
            <TableHead>Comissão</TableHead>
            <TableHead>Pessoas</TableHead>
            <TableHead>Lucro por lead</TableHead>
            <TableHead>Faixa</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {comLancamento.map((grupo) => (
            <TableRow key={grupo.groupId}>
              <TableRowHeader>{grupo.groupName ?? 'Grupo sem nome'}</TableRowHeader>

              <TableCell numeric>
                {grupo.amountCents === null ? (
                  <span className="text-muted-foreground">não lançado</span>
                ) : (
                  <span className="nums">{reaisDeCentavos(grupo.amountCents)}</span>
                )}
              </TableCell>

              <TableCell numeric>
                {grupo.membersEod === null ? (
                  <span className="text-muted-foreground">sem contagem</span>
                ) : (
                  <span className="nums">
                    {formatNumber(grupo.membersEod, { abbreviate: 'never' })}
                  </span>
                )}
              </TableCell>

              <TableCell numeric>
                <MetricValue
                  value={
                    grupo.profitPerLeadMillicents === null
                      ? null
                      : lucroPorLeadEmReais(grupo.profitPerLeadMillicents)
                  }
                  format="currency"
                  decimals={4}
                  noDataLabel={
                    grupo.membersEod === null
                      ? 'sem contagem de participantes neste dia'
                      : 'sem comissão informada para este grupo'
                  }
                />
              </TableCell>

              <TableCell>
                {grupo.band === null ? (
                  <span className="text-xs text-muted-foreground">—</span>
                ) : (
                  <FaixaBadge band={grupo.band} />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </section>
  );
}
